import React from "react";
import { produce } from "immer";
import Input from "../../../Input";
import { nanoid } from "nanoid";
import { RiCloseLine } from "react-icons/ri";
import Select from 'react-select';
const DIAMETER_COUNT = 4;

// `diameterText` / `lengthText` hold what the clerk typed (so empty or partial input like
// "12." survives); `diameter1`–`diameter4` and `length` are the numbers sent to the API.
const getDiameters = (p) =>
  p.diameterText ||
  Array.from({ length: DIAMETER_COUNT }, (_, i) => {
    const d = p[`diameter${i + 1}`];
    return d ? String(d) : "";
  });

const getLength = (p) => p.lengthText ?? (p.length ? String(p.length) : "");

// Measurements are non-negative numbers: block the keys a number input would otherwise
// accept for signs and exponents, and drop pasted values that are negative or not numbers.
const BLOCKED_KEYS = ["-", "+", "e", "E"];
const measurementProps = {
  type: "number",
  min: "0",
  step: "any",
  inputMode: "decimal",
  onKeyDown: (e) => {
    if (BLOCKED_KEYS.includes(e.key)) e.preventDefault();
  },
  onWheel: (e) => e.currentTarget.blur(),
};
const isValidMeasurement = (value) => value === "" || (!Number.isNaN(Number(value)) && Number(value) >= 0);

export const withoutDisplayFields = ({ diameterText, lengthText, ...item }) => item;

// The client's method: average the four diameters and round down to a whole cm,
// then Volume (m³) = π/4 × D(m)² × Length(m). Diameters and length are entered in cm.
const recalcVolume = (item) => {
  const [d1, d2, d3, d4] = getDiameters(item).map((d) => parseFloat(d) || 0);
  const averageDiameterCm = Math.floor((d1 + d2 + d3 + d4) / 4);
  const diameterInMetres = averageDiameterCm / 100;
  const lengthInMetres = (parseFloat(item.length) || 0) / 100;
  const volume = (Math.PI / 4) * Math.pow(diameterInMetres, 2) * lengthInMetres;
  item.volume = Math.round(volume * 1000) / 1000;
};

const TreeFellItems = (props) => {
  return (
    <div className="w-full">
      {props.treeFellItems.map((p, index) => {
        return (
          <div className="flex flex-wrap items-end gap-3" key={p.id}>
            <label className="block">
              <span className="block mb-3 text-gray-700 text-md">
                Species
              </span>
              <Select
                id={index}
                options={props.speciesOptions.options}
                value={props.speciesOptions.optionFor(p)}
                name="speciesId"
                onChange={(option) => {
                  props.setTreeFellItems((currentTreeFellItems) =>
                    produce(currentTreeFellItems, (v) => {
                      v[index].speciesId = option.value;
                    })
                  );
                }}
                menuPlacement="auto"
                menuPosition="fixed"
                placeholder="Species"
                className="treeSelect"
              />
              
            </label>
            <label className="block">
              <span className="block mb-3 text-gray-700 text-md">
                Stock Number
              </span>
              <Input
                onChange={(e) => {
                  const description = e.target.value;
                  props.setTreeFellItems((currentTreeFellItems) =>
                    produce(currentTreeFellItems, (v) => {
                      v[index].description = description;
                    })
                  );
                }}
                value={p.description}
                // placeholder="Description"
              />
            </label>
            {getDiameters(p).map((diameter, dIndex) => (
              <label className="block" key={dIndex}>
                <span className="block mb-3 text-gray-700 text-md">
                  Diameter {dIndex + 1}(cm)
                </span>
                <Input
                  {...measurementProps}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!isValidMeasurement(value)) return;
                    props.setTreeFellItems((currentTreeFellItems) =>
                      produce(currentTreeFellItems, (v) => {
                        const diameters = [...getDiameters(v[index])];
                        diameters[dIndex] = value;
                        v[index].diameterText = diameters;
                        v[index].diameter1 = parseFloat(diameters[0]) || 0;
                        v[index].diameter2 = parseFloat(diameters[1]) || 0;
                        v[index].diameter3 = parseFloat(diameters[2]) || 0;
                        v[index].diameter4 = parseFloat(diameters[3]) || 0;
                        recalcVolume(v[index]);
                      })
                    );
                  }}
                  value={diameter}
                />
              </label>
            ))}
            <label className="block">
              <span className="block mb-3 text-gray-700 text-md">Length(cm)</span>
              <Input
                {...measurementProps}
                onChange={(e) => {
                  const length = e.target.value;
                  if (!isValidMeasurement(length)) return;
                  props.setTreeFellItems((currentTreeFellItems) =>
                    produce(currentTreeFellItems, (v) => {
                      v[index].lengthText = length;
                      v[index].length = parseFloat(length) || 0;
                      recalcVolume(v[index]);
                    })
                  );
                }}
                value={getLength(p)}
                // placeholder="Volume"
              />
            </label>
            <label className="block">
              <span className="block mb-3 text-gray-700 text-md">Volume (m³)</span>
              <Input
                readOnly
                tabIndex={-1}
                className="w-full rounded rounded-sm border border-gray-300 py-1 px-2 outline-none bg-gray-100 text-gray-600"
                value={p.volume}
              />
            </label>
            <button
              className="flex items-center justify-center text-lg text-white bg-red-500 rounded rounded-full w-7 h-7"
              onClick={(e) => {
                e.preventDefault();
                if(p.treeFellItemId){
                  
                  props.deleteAction(p.treeFellItemId)
                }else{
                  props.setTreeFellItems((currentTreeFellItems) =>
                  currentTreeFellItems.filter((x) => x.id !== p.id)
                );
                }
                
              }}
            >
              <RiCloseLine />
            </button>
          </div>
        );
      })}
      <button
        className="self-end w-auto px-2 py-1 my-3 font-semibold text-white rounded rounded-md sm:px-3 bg-tclPrimary/75"
        onClick={(e) => {
          e.preventDefault();
          props.setTreeFellItems((currentTreeFellItems) => [
            ...currentTreeFellItems,
            {
              description: "",
              diameter1: 0,
              diameter2: 0,
              diameter3: 0,
              diameter4: 0,
              quantity: 1,
              volume: 0,
              length: 0,
              speciesId: 0,
              id: nanoid(),
            },
          ]);
        }}
      >
        Add Item
      </button>
      {/* <div>{JSON.stringify(treeFellItems, null, 2)}</div> */}
    </div>
  );
};

export default TreeFellItems;
